import { useMemo } from "react";
import { cn } from "../lib/cn";
import { encodeQrSymbol, qrPixelEdge } from "./qr-symbol";

/** Edge, in CSS pixels, a QR code is drawn at unless the caller asks for another. */
export const QR_DEFAULT_SIZE = 176;

export interface QrCodeProps {
  /** Text to encode, e.g. an `otpauth://` URI. Characters outside printable ASCII are percent-encoded as UTF-8 first. */
  value: string;
  /** Accessible name. Never put `value` in it: a QR code can carry a secret. */
  label: string;
  /** Target edge in CSS pixels, quiet zone included. Drawn at the nearest whole number of pixels per module. */
  size?: number;
  className?: string;
}

/**
 * Scannable QR code as inline SVG, generated in the browser (the value never leaves the page).
 * Always near-black modules on a white ground with a 4-module quiet zone, in every theme: scanners need dark-on-light contrast.
 */
export function QrCode({ value, label, size = QR_DEFAULT_SIZE, className }: QrCodeProps) {
  const symbol = useMemo(() => (value ? encodeQrSymbol(value) : null), [value]);
  if (!symbol) return null;
  const edge = qrPixelEdge(symbol.extent, size);
  return (
    <svg
      role="img"
      aria-label={label}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${String(symbol.extent)} ${String(symbol.extent)}`}
      width={edge}
      height={edge}
      shapeRendering="crispEdges"
      focusable="false"
      className={cn("block h-auto max-w-full", className)}
    >
      <rect width={symbol.extent} height={symbol.extent} className="fill-white" />
      <path d={symbol.path} className="fill-ink-950" />
    </svg>
  );
}
